<script lang="ts" setup>
import type { BlogCollectionItem } from '@nuxt/content';

import type { MainPageBannerType, MainPageNewsItemType, ProductType, SectionType } from '~/utils/api/common';
import { TAGS_SEGMENT } from '~/utils/catalogRoute';
import { collectProductTags, getProductTagSlugs } from '~/utils/tags';

const props = withDefaults(
  defineProps<{
    categoryCode?: string;
    banners: MainPageBannerType[];
    news: MainPageNewsItemType[];
    articles: Pick<BlogCollectionItem, 'path' | 'title' | 'image' | 'category' | 'sortAfterProduct'>[];
    products: ProductType[];
    sections: SectionType[];
    activeTagSlugs?: string[];
  }>(),
  {
    activeTagSlugs: () => [],
  },
);

const router = useRouter();
const route = useRoute();

const activeSection = computed(() => props.sections.find((s) => s.code === props.categoryCode));

const tags = computed(() => collectProductTags(props.products));

// Фильтр «Товары по акции 2+1» живёт в query (?two-plus-one=1): состояние видно
// в ссылке и переживает смену секции/тегов. Сам чекбокс скрывается, когда
// участников нет (акция кончилась или посетитель не в группе акции) — query при
// этом просто перестаёт фильтровать, иначе ссылка от участника группы отдавала
// «не-группе» полностью пустой каталог.
const TWO_PLUS_ONE_QUERY_KEY = 'two-plus-one';
const hasTwoPlusOneProducts = computed(() => props.products.some((el) => el.isTwoPlusOne));
const twoPlusOneActive = computed(() => route.query[TWO_PLUS_ONE_QUERY_KEY] === '1' && hasTwoPlusOneProducts.value);

const onToggleTwoPlusOne = (value: boolean) => {
  router.replace({
    path: route.path,
    query: { ...route.query, [TWO_PLUS_ONE_QUERY_KEY]: value ? '1' : undefined },
  });
};

const filteredProducts = computed(() => {
  const sectionId = activeSection.value?.id;
  const tagSlugs = props.activeTagSlugs;

  return props.products
    .filter((el) => !el.secret)
    .filter((el) => !sectionId || el.sectionIds.includes(sectionId))
    .filter((el) => !twoPlusOneActive.value || el.isTwoPlusOne)
    .filter((el) => {
      if (!tagSlugs.length) return true;
      const productSlugs = getProductTagSlugs(el);
      return tagSlugs.some((slug) => productSlugs.includes(slug));
    });
});

type ArticleItem = {
  data: Pick<BlogCollectionItem, 'path' | 'title' | 'image' | 'category' | 'sortAfterProduct'>;
  type: 'article';
};
type ProductItem = {
  data: ProductType;
  type: 'product';
};

const list = computed(() => {
  const products = filteredProducts.value;
  const articlesList = props.articles || [];

  if (!products.length) return [];

  const result: (ArticleItem | ProductItem)[] = products.map((p) => ({
    data: p,
    type: 'product' as const,
  }));

  if (!articlesList.length) return result;

  const sorted = [...articlesList].reverse();
  for (const article of sorted) {
    const afterCode = article.sortAfterProduct;
    if (!afterCode) continue;

    const productIndex = result.findIndex((item) => item.type === 'product' && item.data.code === afterCode);
    if (productIndex === -1) continue;

    result.splice(productIndex + 1, 0, {
      data: article,
      type: 'article' as const,
    });
  }

  return result;
});

const DESKTOP_COLS = 4;

const subscribeAfterIndex = computed(() => {
  const total = list.value.length;
  const afterRows = DESKTOP_COLS * 4;
  return Math.min(afterRows, total);
});

const ym = useYM();

// build the URL for a (section, tags) combination, e.g.
//   /catalog/ukhod_za_litsom/tags/protiv-vospaleniy,uvlazhnenie
const buildFilteredUrl = (section: SectionType | undefined, tagSlugs: string[]) => {
  const tagsSuffix = tagSlugs.length ? `/${TAGS_SEGMENT}/${tagSlugs.join(',')}` : '';
  if (!section) return tagSlugs.length ? `/${TAGS_SEGMENT}/${tagSlugs.join(',')}` : '/';
  return `${calculateSectionLink(section)}${tagsSuffix}`;
};

// Смена секции/тегов меняет path — query (в т.ч. two-plus-one) переносим руками.
const pushFilteredUrl = (url: string) => {
  router.push({ path: url, query: route.query });
};

const onSectionChange = (code: string | undefined) => {
  ym.append('reachGoal', 'category');
  const section = code ? props.sections.find((s) => s.code === code) : undefined;
  pushFilteredUrl(buildFilteredUrl(section, props.activeTagSlugs));
};

const onTagsChange = (slugs: string[]) => {
  pushFilteredUrl(buildFilteredUrl(activeSection.value, slugs));
};
</script>

<template>
  <main class="mainPage">
    <SectionWelcomeSlider :banners="banners" />

    <div class="mainPage__categoryTabs">
      <HomeFilterBar
        :sections="sections"
        :tags="tags"
        :active-section-code="categoryCode"
        :active-tag-slugs="activeTagSlugs"
        :show-two-plus-one-filter="hasTwoPlusOneProducts"
        :two-plus-one="twoPlusOneActive"
        @update:active-section-code="onSectionChange"
        @update:active-tag-slugs="onTagsChange"
        @update:two-plus-one="onToggleTwoPlusOne"
      />
    </div>

    <div v-if="list.length" class="mainPage__catalogGrid">
      <template
        v-for="(item, index) in list"
        :key="item.type === 'product' ? `p_${item.data.id}` : `a_${(item.data as any).path}`"
      >
        <LazyIdleSectionSubscribe v-if="index === subscribeAfterIndex" hydrate-on-idle class="mainPage__subscribeInline" />
        <ProductCard v-if="item.type === 'product'" :product="item.data" :image="{ lazy: true }" />
        <BlogCard v-if="item.type === 'article'" :article="item.data as any" class="mainPage__blogCard" />
      </template>
      <LazySectionSubscribe
        v-if="subscribeAfterIndex >= list.length"
        hydrate-on-idle
        class="mainPage__subscribeInline"
      />
    </div>

    <SectionPressMentions class="mainPage__mentions" />
    <LazyIdleSectionNewsSentences hydrate-on-idle :news="news" class="mainPage__newsSection" />
  </main>
</template>

<style lang="scss">
.mainPage {
  &__categoryTabs {
    padding: 30px 20px 0;

    @include mq('lg') {
      padding: 25px 0 0;
    }

    @include mq('sm') {
      // Без overflow-x: auto — иначе контейнер становится скролл-боксом и
      // обрезает выпадающую панель фильтра (она «прячется под баннер»).
      // Новый FilterBar — выпадающие селекты с flex-wrap, горизонтальный
      // скролл не нужен.
      padding: 7px 16px 0;
    }
  }

  &__catalogGrid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 18px;
    row-gap: 40px;
    padding: 47px 20px 0;
    position: relative;

    @include mq('xl') {
      grid-template-columns: repeat(3, 1fr);
    }

    @include mq('md') {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mq('sm') {
      padding: 36px 16px 0;
      column-gap: 10px;
      row-gap: 44px;
    }
  }

  &__subscribeInline {
    grid-column: 1 / -1;
    margin-top: 20px;
    margin-bottom: 20px;

    @include mq('sm') {
      margin-top: 4px;
      margin-bottom: 4px;
    }
  }

  &__blogCard {
    .blogCard__wrapper {
      aspect-ratio: 338 / 444;
    }

    .blogCard__image {
      border-radius: 0;
    }

    .blogCard__info {
      position: static;
      padding: 0;
    }

    .blogCard__title {
      color: #121212;
    }

    @include mq('sm') {
      grid-column: span 2;
    }
  }

  &__newsSection {
    margin-top: 24px;
    margin-bottom: 49px;
  }
}
</style>
